import { toError } from '@cascade/core/utils';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { Component } from 'react';
import type { ErrorInfo } from 'react';
import { toRenderContext } from './ErrorBoundary.helpers.ts';
import type {
  TCaughtErrorBoundaryProps,
  TErrorBoundaryProps,
  TErrorBoundaryState,
} from './ErrorBoundary.types.ts';

class CaughtErrorBoundary extends Component<
  TCaughtErrorBoundaryProps,
  TErrorBoundaryState
> {
  state: TErrorBoundaryState = {};

  static getDerivedStateFromError(thrown: unknown): TErrorBoundaryState {
    return { error: toError(thrown) };
  }

  componentDidCatch(thrown: unknown, info: ErrorInfo): void {
    const error = toError(thrown);
    const context = toRenderContext(info);

    this.props.reporter(error, context);
  }

  reset = (): void => {
    this.props.onReset();
    this.setState({ error: undefined });
  };

  render() {
    const { error } = this.state;
    const { fallback: Fallback, children } = this.props;

    return error ? <Fallback error={error} reset={this.reset} /> : children;
  }
}

export const ErrorBoundary = (props: TErrorBoundaryProps) => (
  <QueryErrorResetBoundary>
    {({ reset }) => <CaughtErrorBoundary {...props} onReset={reset} />}
  </QueryErrorResetBoundary>
);
